<script>
import {defineComponent} from 'vue'
import {ElNotification} from "element-plus";
import moment from "moment";
import EconomicIndicatorChildRows from "@/views/economic/indicators/EconomicIndicatorChildRows.vue";

export default defineComponent({
  name: "EconomicIndicatorForm",
  components: {EconomicIndicatorChildRows},
  props: {
    id: {type: Number},
    economic_classification_id: {type: Number, default: null}
  },
  data() {
    return {
      loading: false,
      form: {
        id: null,
        economic_classification_id: null,
        year: moment(Date.now()).format("YYYY"),
        quarter: null,
        amount: null,
        child_economic_classification_indicators: [],
      },
      quarters: [{value: 1}, {value: 2}, {value: 3}, {value: 4}],
      // flat list: {id, title, level, parentId, expanded, isLeaf, childrenLoaded, loadingChildren}
      allClassifications: [],
      classificationsLoading: false,
      filterQuery: '',
      dialogTableVisible: false,
      rules: {
        economic_classification_id: [{required: true, message: "Majburiy maydon", trigger: "change"}],
        year: [{required: true, message: "Majburiy maydon", trigger: "change"}],
        quarter: [{required: true, message: "Majburiy maydon", trigger: "change"}],
        amount: [{required: true, message: "Majburiy maydon", trigger: "blur"}]
      }
    }
  },
  computed: {
    visibleClassifications() {
      if (this.filterQuery) {
        const q = this.filterQuery.toLowerCase();
        return this.allClassifications.filter(c => c.title.toLowerCase().includes(q));
      }
      const result = [];
      const visit = (parentId) => {
        this.allClassifications
            .filter(c => c.parentId === parentId)
            .forEach(item => {
              result.push(item);
              if (item.expanded) visit(item.id);
            });
      };
      visit(null);
      return result;
    }
  },
  methods: {
    onClick() {
      this.form = {
        id: null,
        economic_classification_id: this.economic_classification_id || null,
        year: moment(Date.now()).format("YYYY"),
        quarter: null,
        amount: null,
        child_economic_classification_indicators: [],
      };
      this.dialogTableVisible = true;
      if (this.id) {
        this.form.id = this.id;
        this.getExpenseIndicatorById();
      }
      if (!this.allClassifications.length) {
        this.loadRoots();
      }
    },

    async loadRoots() {
      this.classificationsLoading = true;
      try {
        const {data} = await this.$http.get('api/v1/economic-classifications', {
          params: {limit: 100, offset: 0}
        });
        this.allClassifications = data.data.map(item => ({
          id: item.id,
          title: item.title,
          level: 0,
          parentId: null,
          expanded: false,
          isLeaf: false,
          childrenLoaded: false,
          loadingChildren: false,
        }));
      } catch (e) {
        // ignore
      } finally {
        this.classificationsLoading = false;
      }
    },

    async toggleExpand(item) {
      if (item.isLeaf || item.loadingChildren) return;

      if (!item.childrenLoaded) {
        item.loadingChildren = true;
        try {
          const {data} = await this.$http.get('api/v1/economic-classifications', {
            params: {parent_id: item.id, limit: 100, offset: 0}
          });
          item.childrenLoaded = true;
          if (data.data.length === 0) {
            item.isLeaf = true;
            return;
          }
          const children = data.data.map(child => ({
            id: child.id,
            title: child.title,
            level: item.level + 1,
            parentId: item.id,
            expanded: false,
            isLeaf: false,
            childrenLoaded: false,
            loadingChildren: false,
          }));
          const idx = this.allClassifications.findIndex(c => c.id === item.id);
          this.allClassifications.splice(idx + 1, 0, ...children);
        } catch (e) {
          // ignore
        } finally {
          item.loadingChildren = false;
        }
      }

      if (!item.isLeaf) {
        item.expanded = !item.expanded;
      }
    },

    disabledYear(date) {
      return date.getFullYear() < 2018;
    },

    // Recursively normalize the nested child indicators returned by the API into
    // the editable shape used by the form (years as strings, guaranteed arrays).
    mapChildren(arr) {
      return (arr || []).map(c => ({
        id: c.id,
        economic_classification_id: c.economic_classification_id,
        year: c.year != null ? c.year.toString() : null,
        quarter: c.quarter,
        amount: c.amount,
        child_economic_classification_indicators: this.mapChildren(c.child_economic_classification_indicators),
      }));
    },

    // Recursively build the child indicators payload (numeric year/amount, ids kept
    // only for existing rows so the backend can tell updates from new records).
    mapChildrenPayload(arr) {
      return (arr || []).map(c => ({
        ...(c.id ? {id: c.id} : {}),
        economic_classification_id: c.economic_classification_id,
        year: Number(c.year),
        quarter: c.quarter,
        amount: Number(c.amount),
        child_economic_classification_indicators: this.mapChildrenPayload(c.child_economic_classification_indicators),
      }));
    },

    onFilter(query) {
      this.filterQuery = query;
    },

    onSelectClose() {
      this.filterQuery = '';
    },

    getExpenseIndicatorById() {
      this.loading = true;
      this.$http
          .get(`/api/v1/economic-classification-indicators/${this.form.id}`)
          .then(({data}) => {
            this.form = {
              id: data.id,
              economic_classification_id: data.economic_classification_id,
              year: data.year != null ? data.year.toString() : null,
              quarter: data.quarter,
              amount: data.amount,
              child_economic_classification_indicators: this.mapChildren(data.child_economic_classification_indicators),
            };
          })
          .catch(({response}) => {
            ElNotification({type: "error", title: "Xatolik", message: response.data.message || "Yuklashda xatolik sodir bo'ldi"});
          })
          .finally(() => {
            this.loading = false;
          });
    },

    buildPayload() {
      return {
        ...(this.form.id ? {id: this.form.id} : {}),
        economic_classification_id: this.form.economic_classification_id,
        year: Number(this.form.year),
        quarter: this.form.quarter,
        amount: Number(this.form.amount),
        child_economic_classification_indicators: this.mapChildrenPayload(this.form.child_economic_classification_indicators),
      };
    },

    onSubmit() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.loading = true;
          const payload = this.buildPayload();
          const http = this.form.id
              ? this.$http.put(`/api/v1/economic-classification-indicators/${this.form.id}`, payload)
              : this.$http.post("/api/v1/economic-classification-indicators", payload);

          http.then(() => {
            ElNotification({title: "Tabriklaymiz", message: "Muvafaqiyatli saqlandi", type: "success"});
            this.dialogTableVisible = false;
            this.$emit('getData');
          }).catch(({response}) => {
            ElNotification({title: "Xatolik", message: response.data.message || "Iltimos, qayta urinib ko'ring.", type: "error"});
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    }
  },
})
</script>

<template>
  <el-button v-if="id" type="primary" size="small" @click="onClick">Tahrirlash</el-button>
  <el-button v-else type="primary" size="small" @click="onClick">Qo'shish</el-button>

  <el-dialog
      v-model="dialogTableVisible"
      append-to-body
      destroy-on-close
      :title="id ? 'Ko\'rsatgichni tahrirlash' : 'Ko\'rsatgich qo\'shish'"
      width="800"
  >
    <div class="main-data" v-loading="loading">
      <el-form ref="form" :model="form" :rules="rules" label-position="top">
        <el-row :gutter="10">

          <el-col :span="24">
            <el-form-item label="Klassifikatorni tanlang" prop="economic_classification_id">
              <el-select
                  v-model="form.economic_classification_id"
                  :loading="classificationsLoading"
                  :filter-method="onFilter"
                  filterable
                  clearable
                  placeholder="Klassifikatorni tanlang yoki qidiring..."
                  style="width: 100%"
                  size="large"
                  @visible-change="(v) => !v && onSelectClose()"
              >
                <el-option
                    v-for="item in visibleClassifications"
                    :key="item.id"
                    :label="item.title"
                    :value="item.id"
                    class="cls-option"
                >
                  <div class="cls-row" :style="{ paddingLeft: item.level * 20 + 'px' }">
                    <span
                        class="cls-toggle"
                        @mousedown.stop.prevent
                        @click.stop="toggleExpand(item)"
                    >
                      <template v-if="item.isLeaf">
                        <span class="cls-dot">•</span>
                      </template>
                      <template v-else-if="item.loadingChildren">
                        <span class="cls-spinner">⟳</span>
                      </template>
                      <template v-else>
                        <span class="cls-arrow">{{ item.expanded ? '▾' : '▸' }}</span>
                      </template>
                    </span>
                    <span class="cls-title">{{ item.title }}</span>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="24" :md="12">
            <el-form-item label="Yilni tanlang" prop="year">
              <el-date-picker
                  size="large"
                  style="width: 100%"
                  v-model="form.year"
                  type="year"
                  format="YYYY"
                  value-format="YYYY"
                  :disabled-date="disabledYear"
                  placeholder="Yilni tanlang"
              />
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="24" :md="12">
            <el-form-item label="Chorakni tanlang" prop="quarter">
              <el-select v-model="form.quarter" size="large" style="width: 100%" placeholder="Chorakni tanlang">
                <el-option v-for="item in quarters" :value="item.value" :label="item.value" :key="item.value"/>
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="24">
            <el-form-item label="Miqdorni kiriting" prop="amount">
              <el-input size="large" v-model.number="form.amount" type="number"/>
            </el-form-item>
          </el-col>

          <el-col :span="24">
            <economic-indicator-child-rows
                v-model="form.child_economic_classification_indicators"
                :parent-classification-id="form.economic_classification_id"
                prop-prefix="child_economic_classification_indicators"
                :level="2"
                :max-level="3"
            />
          </el-col>

          <el-col :span="24">
            <el-form-item>
              <el-button size="small" @click="() => dialogTableVisible = false">Yopish</el-button>
              <el-button size="small" type="primary" @click="onSubmit">Saqlash</el-button>
            </el-form-item>
          </el-col>

        </el-row>
      </el-form>
    </div>
  </el-dialog>
</template>

<style scoped>
.cls-option {
  height: auto;
  padding-top: 4px;
  padding-bottom: 4px;
}

.cls-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
}

.cls-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.15s;
}

.cls-toggle:hover {
  background: rgba(0, 0, 0, 0.08);
}

.cls-arrow {
  font-size: 12px;
  color: #606266;
  line-height: 1;
}

.cls-dot {
  font-size: 10px;
  color: #c0c4cc;
}

.cls-spinner {
  font-size: 12px;
  color: #409eff;
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

.cls-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
